/**
 * The state behind the reply editor: the reply being edited, whether it differs from what was last
 * saved, what stops it being published, the live preview, and saving, publishing and switching.
 *
 * Saving always makes a new draft version. Publishing saves first if anything changed, then
 * publishes that version, which also switches the reply on. The preview and the list of problems
 * follow the editing, a moment behind.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import {
  wizardMagicEmailApi,
  wizardMagicEmailKeys,
  type DocumentBlock,
  type EditableReply,
  type ReplyCatalog,
  type ReplyVersion,
} from '../api/api';
import { fromEditor, toEditor } from '../editor/convert';
import { errorText, useReplyCatalog } from './use-magic-email-library';

const PREVIEW_DELAY_MS = 400;

/** The reply as the editor will report it, so an untouched reply never reads as changed. */
const normalized = (content: EditableReply): EditableReply => ({
  ...content,
  document: fromEditor(toEditor(content.document ?? [])),
  documentError: null,
});

/** What is sent: examples left blank while typing are not examples. */
export const payload = (reply: EditableReply): EditableReply => ({
  ...reply,
  exampleEmails: reply.exampleEmails.map(example => example.trim()).filter(Boolean),
});

export type ReplyEditorStatus = 'live' | 'changes' | 'off' | 'draft' | 'unavailable';

export function useReplyEditor(entryId: string) {
  const queryClient = useQueryClient();
  const catalogQuery = useReplyCatalog();
  const detail = useQuery({
    queryKey: wizardMagicEmailKeys.entry(entryId),
    queryFn: () => wizardMagicEmailApi.fetchEntry(entryId),
  });

  const entry = detail.data?.entry;
  const versions: ReplyVersion[] = detail.data?.versions ?? [];
  const latest = versions[0];

  const [reply, setReply] = useState<EditableReply | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [resets, setResets] = useState(0);
  const [justPublished, setJustPublished] = useState<number | null>(null);

  // Starts from the latest version when the reply opens. Saving does not reload: what was saved is
  // already in the editor.
  useEffect(() => {
    if (latest?.content && loadedFor !== entryId) {
      const start = normalized(latest.content);
      setReply(start);
      setSaved(JSON.stringify(start));
      setLoadedFor(entryId);
      setResets(count => count + 1);
    }
  }, [entryId, latest, loadedFor]);

  const dirty = !!reply && saved !== null && JSON.stringify(reply) !== saved;
  const unpublished = dirty || latest?.status === 'draft';

  const change = useCallback((patch: Partial<EditableReply>) => {
    setJustPublished(null);
    setReply(current => (current ? { ...current, ...patch } : current));
  }, []);

  const setDocument = useCallback((document: DocumentBlock[]) => change({ document }), [change]);

  const setStep = useCallback((index: number, step: ReplyCatalog['steps'][number]) => {
    setJustPublished(null);
    setReply(current =>
      current
        ? {
            ...current,
            actions: current.actions.map((action, i) =>
              i === index
                ? {
                    ...action,
                    code: step.code,
                    label: step.label,
                    contract: step.contract,
                    mode: step.mode,
                  }
                : action,
            ),
          }
        : current,
    );
  }, []);

  const loadVersion = useCallback((version: ReplyVersion) => {
    if (!version.content) return;
    setReply(normalized(version.content));
    setResets(count => count + 1);
  }, []);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: wizardMagicEmailKeys.all }),
    [queryClient],
  );

  const save = useMutation({
    mutationFn: (content: EditableReply) =>
      wizardMagicEmailApi.saveDraft({ entryId, content: payload(content) }),
    onSuccess: (_result, content) => {
      setSaved(JSON.stringify(content));
      void refresh();
    },
    onError: error => toast.error(`Not saved: ${errorText(error)}`),
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (!reply) throw new Error('Nothing to publish');
      if (!unpublished)
        return wizardMagicEmailApi
          .setEnabled({ entryId, enabled: true })
          .then(() => entry?.publishedVersion ?? null);
      const version = dirty ? (await save.mutateAsync(reply)).version : latest!.version;
      await wizardMagicEmailApi.publish({ entryId, version });
      return version;
    },
    onSuccess: version => {
      setJustPublished(version);
      void refresh();
    },
    onError: error => toast.error(errorText(error)),
  });

  const turnOff = useMutation({
    mutationFn: () => wizardMagicEmailApi.setEnabled({ entryId, enabled: false }),
    onSuccess: () => {
      toast.success('Turned off. From the next email, a person answers instead.');
      void refresh();
    },
    onError: error => toast.error(errorText(error)),
  });

  // ─── Preview ──────────────────────────────────────────────────────────────────────────
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [highlight, setHighlight] = useState(true);
  const debounced = useDebounce(reply, PREVIEW_DELAY_MS);
  const preview = useQuery({
    queryKey: ['wizardMagicEmail', 'preview', entryId, debounced, flags, highlight],
    queryFn: () => wizardMagicEmailApi.preview({ content: payload(debounced!), flags, highlight }),
    enabled: !!debounced,
    placeholderData: keepPreviousData,
  });

  const problems = preview.data?.problems ?? latest?.problems ?? [];
  const unavailable = problems.filter(problem => problem.fix === 'waterlily');
  const toFix = problems.filter(problem => problem.fix === 'edit');

  const status: ReplyEditorStatus = useMemo(() => {
    if (unavailable.length > 0) return 'unavailable';
    if (entry?.enabled && !unpublished) return 'live';
    if (entry?.publishedVersion && unpublished) return 'changes';
    if (entry?.publishedVersion) return 'off';
    return 'draft';
  }, [entry, unavailable.length, unpublished]);

  return {
    loading: detail.isPending || catalogQuery.isPending || !reply,
    /** Set when the stored words could not be opened in the editor. */
    documentError:
      latest?.content?.documentError ??
      (latest && !latest.content ? 'This version could not be read.' : null),
    error: detail.error ?? catalogQuery.error,
    catalog: catalogQuery.data,
    entry,
    versions,
    reply,
    /** Changes when the editor must start again from `reply.document`. */
    editorKey: `${entryId}:${resets}`,
    change,
    setDocument,
    setStep,
    loadVersion,
    dirty,
    status,
    publishedVersion: entry?.publishedVersion ?? null,
    unavailable,
    toFix,
    save: () => reply && save.mutate(reply, { onSuccess: () => toast.success('Draft saved') }),
    saving: save.isPending,
    canPublish:
      problems.length === 0 && (unpublished || (!entry?.enabled && !!entry?.publishedVersion)),
    publishLabel: unpublished ? 'Publish' : 'Turn on',
    publish: () => publish.mutate(),
    publishing: publish.isPending,
    justPublished,
    canTurnOff: !!entry?.enabled,
    turnOff: () => turnOff.mutate(),
    preview: {
      query: preview,
      flags,
      setFlag: (name: string, value: boolean) =>
        setFlags(current => ({ ...current, [name]: value })),
      highlight,
      setHighlight,
    },
  };
}
